import React, { useEffect, useRef, useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { checkBarcodeStock, DownloadBarcodeValidInvalid } from "Service/ApiService";

const getStatusChip = (value) => {
  const statusValue = value?.toLowerCase();
  const statusConfig = {
    valid: { label: "Valid", color: "#4caf50" },
    invalid: { label: "Invalid", color: "#f44336" },
    not_found: { label: "Not Found", color: "#ff9800" },
  };

  return statusConfig[statusValue] || { label: "Unknown", color: "#9e9e9e" };
};

const CheckBarcodeForStockController = () => {
  const [barcodeText, setBarcodeText] = useState("");
  const [materialDescription, setMaterialDescription] = useState("");
  const [isMaterialDescriptionMode, setIsMaterialDescriptionMode] = useState(false);
  const [file, setFile] = useState(null);
  const [resultData, setResultData] = useState([]);
  const [resultDataGarment, setResultDataGarment] = useState([]);
  const [cloading, setcLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [layoutType, setLayoutType] = useState(null);
  const fileInputRef = useRef();

  
  useEffect(() => {
    const validItems = resultData.filter((item) => item.status === "valid");
    const uniqueMaterialVerticals = [
      ...new Set(validItems.map((item) => item.materialVertical?.toLowerCase())),
    ];

    if (uniqueMaterialVerticals.includes("garment")) {
      setLayoutType("garment");
    } else if (uniqueMaterialVerticals.includes("fabric")) {
      setLayoutType("fabric");
    } else {
      setLayoutType(null);
    }
  }, [resultData]);

  const handleBarcodeChange = (e) => {
    const formatted = e.target.value
      .split(/[\r\n,]+/)
      .map((barcode) => barcode.trim())
      .filter(Boolean)
      .join(", ");

    setBarcodeText(formatted);
    setFile(null);
    setErrorMessage("");
    handleSubmit(formatted);
  };

  const handleMaterialDescriptionChange = (e) => {
    setMaterialDescription(e.target.value);
    setFile(null);
    setErrorMessage("");
  };

  const handleMaterialDescriptionKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(materialDescription);
    }
  };

  const resetSearchState = () => {
    setBarcodeText("");
    setMaterialDescription("");
    setFile(null);
    setResultData([]);
    setResultDataGarment([]);
    setLayoutType(null);
    setErrorMessage("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleToggleInputMode = (_, checked) => {
    setIsMaterialDescriptionMode(checked);
    resetSearchState();
  };

  const handleInputModeChange = (_, mode) => {
    if (!mode) return;
    setIsMaterialDescriptionMode(mode === "material");
    resetSearchState();
  };

  const handleClearButtonClick = () => {
    resetSearchState();
  };

  const handleBarcodeKeyDown = (e) => {
    if (e.key === "Enter") {
      const { selectionStart, value } = e.target;

      if (selectionStart === value.length) {
        e.preventDefault();
        handleSubmit(barcodeText);
        setBarcodeText((prev) => `${prev.trim()}, `);
      }
    }
  };

  const handleBarcodePaste = (e) => {
    e.preventDefault();

    const previousValue = e.target.value.trim();
    const pastedText = e.clipboardData.getData("text");
    const formattedText = pastedText
      .split(/[\n\r]+/)
      .map((item) => item.trim())
      .filter(Boolean)
      .join(", ");
    const newValue = previousValue ? `${previousValue}, ${formattedText}` : formattedText;

    setBarcodeText(newValue);
    handleSubmit(newValue);
  };

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    setErrorMessage("");

    if (!uploadedFile || uploadedFile.type !== "text/plain") {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setErrorMessage("Please upload a valid .txt file.");
      return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      const formattedBarcodes = event.target.result
        .split(/\r?\n|,/)
        .map((barcode) => barcode.trim())
        .filter(Boolean)
        .join(", ");

      setBarcodeText(formattedBarcodes);
      setFile(uploadedFile);
      handleSubmit(formattedBarcodes);
    };

    reader.onerror = () => {
      setErrorMessage("Error reading the file. Please try again.");
    };

    reader.readAsText(uploadedFile);
  };

  const handleSubmit = async (values) => {
    if (values && values.preventDefault) {
      values.preventDefault();
      values = isMaterialDescriptionMode ? materialDescription : barcodeText;
    }

    const normalizedValue = values?.trim?.() || "";

    if (!normalizedValue) {
      setErrorMessage(
        isMaterialDescriptionMode
          ? "Please enter material description."
          : "Please enter or upload barcodes."
      );
      return;
    }

    setErrorMessage("");
    setcLoading(true);

    try {
      const barcodeParam = isMaterialDescriptionMode
        ? ""
        : normalizedValue
            .split(/\r?\n|,/)
            .map((barcode) => barcode.trim())
            .filter(Boolean)
            .reverse()
            .join(",");
      const materialDescParam = isMaterialDescriptionMode ? normalizedValue : "";
      const data = await checkBarcodeStock(barcodeParam, materialDescParam);

      if (data?.success && Array.isArray(data.data)) {
        let lastKey = "";
        let groupIndex = 0;
        const formatted = data.data.flatMap((item, index) => {
          const key = `${item.materialCode}`;

          if (key !== lastKey) {
            groupIndex += 1;
            lastKey = key;
          }

          return item.sizeData?.length > 0
            ? item.sizeData.map((sizeItem, sizeIndex) => ({
                id: `${index + 1}-${sizeIndex + 1}`,
                barcode: item.barcode || "-",
                materialCode: item.materialCode || "-",
                mainGroup: item.mainGroup || "-",
                materialDesc: item.materialDesc || "-",
                materialVertical: item.materialVertical || "-",
                shade: item.shade || "-",
                brand: item.brand || "-",
                category: item.category || "-",
                wearType: item.wearType || "-",
                status: item.status || "-",
                sizex: sizeItem.sizex || "-",
                balanceQty: sizeItem.balanceQty || "-",
                groupIndex,
              }))
            : [
                {
                  id: `${index + 1}-0`,
                  barcode: item.barcode || "-",
                  materialCode: item.materialCode || "-",
                  mainGroup: item.mainGroup || "-",
                  materialDesc: item.materialDesc || "-",
                  materialVertical: item.materialVertical || "-",
                  shade: item.shade || "-",
                  brand: item.brand || "-",
                  category: item.category || "-",
                  wearType: item.wearType || "-",
                  status: item.status || "-",
                  sizex: "-",
                  balanceQty: "-",
                  groupIndex,
                },
              ];
        });

        setResultDataGarment(data.data);
        setResultData(formatted);
      } else {
        setResultData([]);
        setResultDataGarment([]);
        setErrorMessage(data?.msg || "No records found.");
      }
    } catch (error) {
      console.error("API error:", error);
      setErrorMessage("Failed to fetch barcode data. Please try again.");
    } finally {
      setcLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      if (isMaterialDescriptionMode && !materialDescription.trim()) {
        setErrorMessage("Please enter material description.");
        return;
      }

      if (!isMaterialDescriptionMode && !barcodeText && !file) {
        setErrorMessage("Please enter or upload barcodes.");
        return;
      }

      setcLoading(true);

      const barcodeParam = !isMaterialDescriptionMode && barcodeText
        ? barcodeText
            .split(/\r?\n|,/)
            .map((barcode) => barcode.trim())
            .filter(Boolean)
            .reverse()
            .join(",")
        : "";
      const materialDescParam = isMaterialDescriptionMode ? materialDescription.trim() : "";

      await DownloadBarcodeValidInvalid(
        isMaterialDescriptionMode ? null : barcodeParam || null,
        isMaterialDescriptionMode ? materialDescParam : null
      );
    } catch (error) {
      console.error("CSV download failed:", error);
      setErrorMessage("Failed to download CSV. Please try again.");
    } finally {
      setcLoading(false);
    }
  };

  const columns = [
    { field: "materialCode", headerName: "Material Code", flex: 0.9, minWidth: 80, headerAlign: "center", align: "center" },
    { field: "mainGroup", headerName: "Main Group", flex: 0.9, minWidth: 75, headerAlign: "center", align: "center" },
    { field: "materialDesc", headerName: "Material Description", flex: 1.4, minWidth: 120, headerAlign: "center", align: "center" },
    { field: "barcode", headerName: "Barcode", flex: 0.9, minWidth: 80, headerAlign: "center", align: "center" },
    { field: "brand", headerName: "Brand", flex: 0.8, minWidth: 70, headerAlign: "center", align: "center" },
    { field: "category", headerName: "Category", flex: 0.8, minWidth: 70, headerAlign: "center", align: "center" },
    { field: "sizex", headerName: "Size", flex: 0.6, minWidth: 55, headerAlign: "center", align: "center" },
    { field: "balanceQty", headerName: "Stock", flex: 0.6, minWidth: 55, headerAlign: "center", align: "center" },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 92,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const { label, color } = getStatusChip(params.value);
        return (
          <Chip
            label={label}
            style={{ backgroundColor: color, color: "#fff", fontWeight: "bold", minWidth: 82 }}
            size="small"
          />
        );
      },
    },
  ];

  const maxSizes = Math.max(0, ...resultDataGarment.map((item) => item.sizeData?.length || 0));
  const firstValidRow = resultDataGarment.find((item) => item.status === "valid" && item.sizeData?.length > 0);
  const baseColumns = [
    { align: "center", field: "mainGroup", headerName: "Main Group", flex: 0.8, minWidth: 70 },
    { align: "center", field: "shade", headerName: "Shade", flex: 0.8, minWidth: 65 },
    { align: "center", field: "barcode", headerName: "Barcode", flex: 0.9, minWidth: 75 },
    { align: "center", field: "materialDesc", headerName: "Material Description", flex: 1.3, minWidth: 110 },
    { align: "center", field: "brand", headerName: "Brand", flex: 0.8, minWidth: 65 },
    { align: "center", field: "category", headerName: "Category", flex: 0.8, minWidth: 65 },
    { align: "center", field: "wearType", headerName: "Wear Type", flex: 0.8, minWidth: 70 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 92,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const { label, color } = getStatusChip(params.value);
        return (
          <Chip
            label={label}
            style={{ backgroundColor: color, color: "#fff", fontWeight: "bold", minWidth: 82 }}
            size="small"
          />
        );
      },
    },
  ];
  const sizeColumns = Array.from({ length: maxSizes }, (_, index) => ({
    field: `size_${index}`,
    headerName: firstValidRow?.sizeData?.[index]?.sizex || `Size ${index + 1}`,
    flex: 0.5,
    align: "center",
    headerAlign: "center",
    minWidth: 45,
    renderCell: (params) => (
      <Box
        border={2}
        p={0.5}
        sx={{ borderRadius: "6px 6px 0 0", background: "#f2f2f2", width: "100%", textAlign: "center" }}
      >
        <Typography variant="body2" sx={{ color: "black", fontSize: "0.78rem" }}>
          {params.value ?? "-"}
        </Typography>
      </Box>
    ),
  }));
  const columnsGarment = [...baseColumns, ...sizeColumns];

  let lastGroupValue = null;
  let groupIndex = 0;
  const rowsGarment = resultDataGarment.map((item, index) => {
    const currentGroupValue = item.materialCode;

    if (currentGroupValue !== lastGroupValue) {
      groupIndex += 1;
      lastGroupValue = currentGroupValue;
    }

    const row = {
      id: index + 1,
      groupIndex,
      barcode: item.barcode,
      mainGroup: item.mainGroup || "",
      shade: item.shade || "",
      materialDesc: item.materialDesc || "",
      brand: item.brand || "",
      category: item.category || "",
      wearType: item.wearType || "",
      status: item.status || "not_found",
    };

    for (let i = 0; i < maxSizes; i += 1) {
      row[`size_${i}`] = item.sizeData?.[i]?.balanceQty || 0;
    }

    return row;
  });

  return {
    barcodeText,
    materialDescription,
    isMaterialDescriptionMode,
    handleBarcodeChange,
    handleMaterialDescriptionChange,
    handleBarcodeKeyDown,
    handleBarcodePaste,
    handleFileUpload,
    handleToggleInputMode,
    handleInputModeChange,
    handleClearButtonClick,
    handleSubmit,
    handleDownloadCSV,
    handleMaterialDescriptionKeyDown,
    resultData,
    cloading,
    columns,
    fileInputRef,
    errorMessage,
    columnsGarment,
    rowsGarment,
    layoutType,
  };
};

export default CheckBarcodeForStockController;
